
import React from 'react';
import { Button } from '@/components/ui/button';
import { Plus, LogOut } from 'lucide-react';
import { useIsMobile } from '@/hooks/use-mobile';
import { useMembersState } from './hooks/members/useMembersState';
import MembersTable from './MembersTable';
import MemberMobileList from './memberMobile/MemberMobileList';
import InviteMemberDialog from './dialogs/InviteMemberDialog';
import DeleteMemberDialog from './dialogs/DeleteMemberDialog';
import PrimaryOwnerDialog from './dialogs/PrimaryOwnerDialog';
import LeavePortfolioDialog from './dialogs/LeavePortfolioDialog';
import TransferOwnershipDialog from './dialogs/TransferOwnershipDialog';

interface MembersSettingsProps {
  source?: 'portfolio' | 'business';
}

const MembersSettings: React.FC<MembersSettingsProps> = ({ source = 'portfolio' }) => {
  const isMobile = useIsMobile();
  
  // Call the hook unconditionally at the top level
  const membersState = useMembersState(source);
  console.log('MembersSettings: Successfully loaded members state');
  
  const {
    members,
    editingMemberId,
    isCurrentMemberPrimaryOwner,
    isCurrentMemberOwner,
    hasBusinessMembersPermission,
    canManageBusinessMembers,
    inviteMemberForm,
    getCheckedPermissionsCount,
    getCheckedPermissionNames,
    showAddMemberDialog,
    setShowAddMemberDialog,
    showDeleteMemberDialog,
    setShowDeleteMemberDialog,
    showPrimaryOwnerDialog,
    setShowPrimaryOwnerDialog,
    showLeavePortfolioDialog,
    setShowLeavePortfolioDialog,
    showTransferOwnershipDialog,
    setShowTransferOwnershipDialog,
    memberToDelete,
    setMemberToDelete,
    memberToPromote,
    setMemberToPromote,
    handleAddMember,
    handleAddMemberSubmit,
    handleRemoveMemberClick,
    handleEditClick,
    handleCancelEdit,
    handleSaveEdit,
    handleRoleChange,
    handlePermissionChange,
    handleConfirmDelete,
    handleConfirmOwnershipTransfer,
    handleRemoveCurrentUser,
    handleLeavePortfolio,
    handleResendInvite,
  } = membersState;

  // Text based on source context
  const leaveText = source === 'portfolio' ? 'Leave Portfolio' : 'Leave Business';

  return (
    <div className="space-y-6 py-6">
      <div className="flex flex-wrap justify-between items-center gap-2">
        <div>
          <h2 className="text-2xl font-bold tracking-tight">{source === 'portfolio' ? 'Portfolio' : 'Business'} Members</h2>
          <p className="text-muted-foreground mt-1">
            Manage members and their permissions for this {source}.
          </p>
        </div>
        
        <div className="flex flex-wrap gap-2">
          {/* Only show Add Member button if user has appropriate permissions */}
          {(isCurrentMemberPrimaryOwner || isCurrentMemberOwner || 
            (source === 'business' ? hasBusinessMembersPermission : true)) && (
            <Button 
              onClick={() => {
                console.log('Add Member button clicked');
                if (handleAddMember) {
                  handleAddMember();
                } else {
                  console.error('handleAddMember function is undefined');
                }
              }} 
              className="gap-1"
            >
              <Plus className="h-4 w-4" />
              <span>Add Member</span>
            </Button>
          )}
          
          {/* Add Leave Portfolio button */}
          {members.length > 0 && (
            <Button 
              variant="outline" 
              onClick={handleRemoveCurrentUser}
              className="text-red-500 hover:text-red-700 border-red-200 hover:bg-red-50"
            >
              <LogOut className="mr-2 h-4 w-4" />
              {leaveText}
            </Button>
          )}
        </div>
      </div>
      
      {isMobile ? (
        <MemberMobileList 
          members={members}
          editingMemberId={editingMemberId}
          isCurrentMemberPrimaryOwner={isCurrentMemberPrimaryOwner}
          getCheckedPermissionsCount={getCheckedPermissionsCount}
          getCheckedPermissionNames={getCheckedPermissionNames}
          handleRemoveMemberClick={handleRemoveMemberClick}
          handleEditClick={handleEditClick}
          handleCancelEdit={handleCancelEdit}
          handleSaveEdit={handleSaveEdit}
          handleRoleChange={handleRoleChange}
          handlePermissionChange={handlePermissionChange}
          canManageBusinessMembers={canManageBusinessMembers}
          handleResendInvite={handleResendInvite}
        />
      ) : (
        <MembersTable 
          members={members}
          editingMemberId={editingMemberId}
          isCurrentMemberPrimaryOwner={isCurrentMemberPrimaryOwner}
          getCheckedPermissionsCount={getCheckedPermissionsCount}
          getCheckedPermissionNames={getCheckedPermissionNames}
          handleRemoveMemberClick={handleRemoveMemberClick}
          handleEditClick={handleEditClick}
          handleCancelEdit={handleCancelEdit}
          handleSaveEdit={handleSaveEdit}
          handleRoleChange={handleRoleChange}
          handlePermissionChange={handlePermissionChange}
          canManageBusinessMembers={canManageBusinessMembers}
          handleResendInvite={handleResendInvite}
        />
      )}
      
      {/* Dialogs */}
      <InviteMemberDialog 
        open={showAddMemberDialog}
        onOpenChange={setShowAddMemberDialog}
        formState={inviteMemberForm}
        onSubmit={handleAddMemberSubmit}
        source={source}
      />
      
      <DeleteMemberDialog 
        open={showDeleteMemberDialog}
        onOpenChange={setShowDeleteMemberDialog}
        member={memberToDelete}
        onConfirm={handleConfirmDelete}
        source={source}
      />
      
      <PrimaryOwnerDialog
        open={showPrimaryOwnerDialog}
        onOpenChange={setShowPrimaryOwnerDialog}
        source={source}
      />
      
      <LeavePortfolioDialog
        open={showLeavePortfolioDialog}
        onOpenChange={setShowLeavePortfolioDialog}
        onConfirm={handleLeavePortfolio}
        source={source}
      />
      
      <TransferOwnershipDialog
        open={showTransferOwnershipDialog}
        onOpenChange={setShowTransferOwnershipDialog}
        onConfirm={handleConfirmOwnershipTransfer}
        member={memberToPromote}
        source={source}
      />
    </div>
  );
};

export default MembersSettings;
